import * as THREE from 'three';
import { HealthBar } from '../../HealthBar.js';
import { colored } from '../../Style.js';
import { SCIFI, armor, glow, glowColor, bake, Gait } from './units-style.js';

// The rail rifle is carried upright at the side, which keeps its long barrel
// inside the railgunner's small collision radius
const RIFLE_POSE = -0.25;

// Railgunner: a lean armoured trooper with a team cloak and shoulder plate,
// carrying a long rail rifle ringed with glowing coils
export class Sniper {
  constructor(scene, unit, color) {
    this.scene = scene;
    this.unit = unit;
    this.color = color;
    this.mesh = null;
    this.lastPosition = null;
    this.targetRotation = 0;
    this.healthBar = null;
    this.maxHealth = 15; // SniperHealth from server
    this.geometries = [];
    this.create();
  }

  // Armour and glow parts merged into one mesh each
  part(parts, glowParts = []) {
    const group = new THREE.Group();
    const geometry = bake(parts);
    this.geometries.push(geometry);
    const mesh = new THREE.Mesh(geometry, armor());
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
    if (glowParts.length) {
      const glowGeometry = bake(glowParts);
      this.geometries.push(glowGeometry);
      group.add(new THREE.Mesh(glowGeometry, glow()));
    }
    return group;
  }

  // A pivoting limb attached to the body
  limb(parts, glowParts, x, y, z) {
    const pivot = new THREE.Group();
    pivot.position.set(x, y, z);
    pivot.add(this.part(parts, glowParts));
    this.body.add(pivot);
    return pivot;
  }

  create() {
    const bodyGroup = new THREE.Group();

    const team = new THREE.Color(this.color);
    const teamDark = team.clone().multiplyScalar(0.6);
    const trim = glowColor(this.color);

    // Everything that bobs while walking hangs off the body
    this.body = new THREE.Group();
    bodyGroup.add(this.body);

    // Slim gunmetal suit, team chest stripe and left shoulder plate,
    // a narrow helmet with a visor slit and a rangefinder eyepiece
    this.body.add(this.part([
      colored(new THREE.BoxGeometry(0.66, 0.36, 0.44), SCIFI.alloyDark, { y: 0.88 }),
      colored(new THREE.BoxGeometry(0.74, 0.8, 0.46), SCIFI.gunmetal, { y: 1.36 }),
      colored(new THREE.BoxGeometry(0.2, 0.7, 0.08), team, { y: 1.4, z: 0.24 }),
      colored(new THREE.BoxGeometry(0.78, 0.08, 0.5), SCIFI.alloy, { y: 1.05 }),
      colored(new THREE.BoxGeometry(0.46, 0.2, 0.14), SCIFI.white, { y: 1.62, z: 0.2 }),
      colored(new THREE.BoxGeometry(0.34, 0.24, 0.46), team, { x: -0.4, y: 1.76, rz: 0.35 }),
      colored(new THREE.BoxGeometry(0.26, 0.16, 0.36), SCIFI.white, { x: 0.38, y: 1.76, rz: -0.35 }),
      colored(new THREE.CylinderGeometry(0.14, 0.16, 0.14, 6), SCIFI.alloyDark, { y: 1.8 }),
      colored(new THREE.BoxGeometry(0.44, 0.44, 0.5), SCIFI.white, { y: 2.06 }),
      colored(new THREE.BoxGeometry(0.4, 0.2, 0.4), SCIFI.gunmetal, { y: 1.92, z: 0.1 }),
      colored(new THREE.BoxGeometry(0.46, 0.12, 0.06), SCIFI.black, { y: 2.1, z: 0.25 }),
      colored(new THREE.BoxGeometry(0.1, 0.12, 0.26), SCIFI.gunmetal, { x: 0.27, y: 2.12, z: 0.12 }),
      colored(new THREE.BoxGeometry(0.06, 0.26, 0.06), team, { y: 2.32, z: -0.1 })
    ], [
      colored(new THREE.BoxGeometry(0.38, 0.06, 0.04), trim, { y: 2.1, z: 0.28 }),
      colored(new THREE.BoxGeometry(0.08, 0.08, 0.04), SCIFI.cyan, { x: 0.27, y: 2.12, z: 0.26 })
    ]));

    // Legs: armoured trousers and boots, pivoting at the hip
    const leg = () => [
      colored(new THREE.BoxGeometry(0.28, 0.42, 0.3), SCIFI.gunmetal, { y: -0.22 }),
      colored(new THREE.BoxGeometry(0.26, 0.16, 0.08), SCIFI.white, { y: -0.42, z: 0.16 }),
      colored(new THREE.BoxGeometry(0.3, 0.32, 0.4), SCIFI.alloyDark, { y: -0.64, z: 0.04 })
    ];
    this.leftLeg = this.limb(leg(), [], -0.18, 0.8, 0);
    this.rightLeg = this.limb(leg(), [], 0.18, 0.8, 0);

    // Arms: undersuit and white bracer
    const arm = () => [
      colored(new THREE.BoxGeometry(0.2, 0.48, 0.22), SCIFI.gunmetal, { y: -0.26 }),
      colored(new THREE.BoxGeometry(0.22, 0.24, 0.24), SCIFI.white, { y: -0.56 }),
      colored(new THREE.BoxGeometry(0.2, 0.14, 0.2), SCIFI.alloyDark, { y: -0.72 })
    ];
    this.leftArm = this.limb(arm(), [], -0.46, 1.62, 0);

    // Rail rifle built along +Z around the grip, then stood upright in the right hand:
    // a receiver, twin rails with a glowing channel between and coil rings
    const rifle = [
      colored(new THREE.BoxGeometry(0.14, 0.2, 0.7), SCIFI.alloyDark, { y: 0.0, z: 0.0 }),
      colored(new THREE.BoxGeometry(0.12, 0.18, 0.36), SCIFI.gunmetal, { y: -0.04, z: -0.5 }),
      colored(new THREE.BoxGeometry(0.08, 0.2, 0.1), SCIFI.gunmetal, { y: -0.16, z: 0.1 }),
      colored(new THREE.BoxGeometry(0.04, 0.05, 1.1), SCIFI.alloy, { y: 0.07, z: 0.88 }),
      colored(new THREE.BoxGeometry(0.04, 0.05, 1.1), SCIFI.alloy, { y: -0.05, z: 0.88 }),
      colored(new THREE.BoxGeometry(0.08, 0.1, 0.3), SCIFI.black, { y: 0.17, z: 0.05 }),
      colored(new THREE.BoxGeometry(0.12, 0.16, 0.08), team, { y: 0.01, z: 1.45 })
    ];
    const rifleGlow = [
      colored(new THREE.BoxGeometry(0.025, 0.04, 1.05), trim, { y: 0.01, z: 0.88 }),
      colored(new THREE.BoxGeometry(0.04, 0.04, 0.04), SCIFI.cyan, { y: 0.17, z: 0.21 })
    ];
    for (const z of [0.5, 0.75, 1.0, 1.25]) {
      rifleGlow.push(colored(new THREE.TorusGeometry(0.1, 0.025, 3, 6), trim, { y: 0.01, z }));
    }
    const pose = geo => {
      geo.rotateX(-Math.PI / 2 + 0.12);
      geo.rotateX(-RIFLE_POSE);
      geo.translate(-0.02, -0.7, 0.06);
      return geo;
    };
    this.rightArm = this.limb([...arm(), ...rifle.map(pose)], rifleGlow.map(pose), 0.44, 1.62, 0);
    this.rightArm.rotation.x = RIFLE_POSE;

    // Split cloak in team colour hanging from the shoulders so it can sway
    this.cloak = this.limb([
      colored(new THREE.BoxGeometry(0.7, 0.1, 0.16), teamDark, {}),
      colored(new THREE.BoxGeometry(0.34, 0.95, 0.05), team, { x: -0.18, y: -0.5, rz: 0.04 }),
      colored(new THREE.BoxGeometry(0.34, 0.85, 0.05), team, { x: 0.18, y: -0.45, rz: -0.04 })
    ], [
      colored(new THREE.BoxGeometry(0.3, 0.04, 0.06), trim, { x: -0.18, y: -0.96 }),
      colored(new THREE.BoxGeometry(0.3, 0.04, 0.06), trim, { x: 0.18, y: -0.86 })
    ], 0, 1.74, -0.27);
    this.cloak.rotation.x = 0.06;

    // Create health bar
    this.healthBar = new HealthBar(this.maxHealth, 1.6, 0.25);
    this.healthBar.getGroup().position.y = 2.56;
    bodyGroup.add(this.healthBar.getGroup());

    this.mesh = bodyGroup;

    // Set initial position
    this.mesh.position.set(this.unit.position.x, this.unit.position.y, this.unit.position.z);
    this.lastPosition = { ...this.unit.position };
    this.gait = new Gait(this.mesh);

    this.scene.add(this.mesh);
  }

  updatePosition(position) {
    if (this.mesh) {
      // Calculate rotation based on movement direction
      if (this.lastPosition) {
        const dx = position.x - this.lastPosition.x;
        const dz = position.z - this.lastPosition.z;
        const distanceMoved = Math.sqrt(dx * dx + dz * dz);

        // Only update rotation if we've moved significantly
        if (distanceMoved > 0.01) {
          this.targetRotation = Math.atan2(dx, dz);
        }
      }

      // Smoothly interpolate rotation
      let rotationDiff = this.targetRotation - this.mesh.rotation.y;
      while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
      while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
      this.mesh.rotation.y += rotationDiff * 0.15;

      // Store last position
      this.lastPosition = { x: position.x, y: position.y, z: position.z };

      this.animate();
    }
  }

  // Walk cycle driven by how far the interpolated mesh moved since the last frame
  animate() {
    if (this.gait.step(2.5, 3.2) <= 0) return;
    const { walk, phase } = this.gait;
    const now = performance.now() / 1000;

    // Short strides and a restrained cloak keep the trooper inside its collision radius
    const swing = Math.sin(phase) * 0.45 * walk;
    this.leftLeg.rotation.x = swing;
    this.rightLeg.rotation.x = -swing;
    this.leftArm.rotation.x = -swing * 0.6;
    this.rightArm.rotation.x = RIFLE_POSE + Math.sin(phase * 2) * 0.04 * walk;
    this.body.position.y = -Math.abs(Math.sin(phase)) * 0.04 * walk;
    this.cloak.rotation.x = 0.06 + walk * 0.16 + Math.sin(now * 1.7) * 0.03 + Math.sin(phase * 2) * 0.04 * walk;
  }

  // Set target position for aiming
  setTarget(targetPosition) {
    if (this.mesh && targetPosition) {
      const dx = targetPosition.x - this.mesh.position.x;
      const dz = targetPosition.z - this.mesh.position.z;
      this.targetRotation = Math.atan2(dx, dz);
    }
  }

  // Update health bar
  updateHealth(health, camera) {
    if (this.healthBar) {
      this.healthBar.update(health, camera);
    }
  }

  remove() {
    if (this.healthBar) {
      this.healthBar.dispose();
    }
    // Materials are shared; only geometries are ours
    this.geometries.forEach(g => g.dispose());
    this.geometries = [];
    if (this.mesh) {
      this.scene.remove(this.mesh);
    }
  }
}
